import React from "react";
import moment from "moment";

import Icon from "./icons";
import { DATE_FORMATS, DATE_OPTIONS, DEFAULT_DATE_FORMAT } from "./resourceTypes";
import { emptyDateConfig } from "./payload";

/**
 * The second row a Key/Type/Value row grows when its type is Date.
 *
 * One component for every table that has data types — Parameters, Headers, Form
 * Data and x-www-form-urlencoded — because ParamsTable is shared by all four.
 * It replaces the antd form's DateConfigSelect and writes the identical object:
 *
 *   { dateOption: "fixedDate" | "today" | "daysBefore",
 *     timeUnits:  number,          // days, for daysBefore
 *     fixedDate:  string | null,   // already formatted with dateFormat
 *     dateFormat: string }
 *
 * Controlled, unlike DateConfigSelect — the row owns the config and this only
 * renders it. The old one mirrored every field into local state and re-synced
 * from props, which is why changing a format there could leave the two copies
 * disagreeing.
 *
 * What each option means at run time (five request builders evaluate it, all
 * identically — editorSlice, boardSlice, the two widget lists and ResourceEditor):
 *
 *   daysBefore   N days back from now, start of day, UTC. Worked out per run.
 *   today        now, worked out per run.
 *   fixedDate    one pinned date, formatted once here and stored.
 */
export default function DateConfigRow({ config, onChange, error }) {
  const value = config || emptyDateConfig();
  const format = value.dateFormat || DEFAULT_DATE_FORMAT;

  const set = (patch) => onChange({ ...value, ...patch });

  /* Switching option starts that option clean rather than carrying the last
     one's number or date across — the antd form does the same. Today is
     resolved immediately so the stored config is never a "today" with no date
     on it. */
  const setOption = (dateOption) =>
    onChange({
      ...value,
      dateOption,
      timeUnits: 1,
      fixedDate: dateOption === "today" ? moment().format(format) : null,
    });

  /* Re-formatting has to move the stored date with it, or the value keeps the
     old format's shape while the row claims the new one. daysBefore has nothing
     to re-format — its date is worked out at run time. */
  const setFormat = (dateFormat) => {
    let fixedDate = value.fixedDate;
    if (value.dateOption === "today") {
      fixedDate = moment().format(dateFormat);
    } else if (value.dateOption === "fixedDate" && value.fixedDate) {
      const parsed = moment(value.fixedDate, format);
      fixedDate = parsed.isValid() ? parsed.format(dateFormat) : value.fixedDate;
    }
    onChange({ ...value, dateFormat, fixedDate });
  };

  /* The picker works in plain dates; the config stores whatever dateFormat says.
     An unparseable stored value leaves the picker empty rather than showing
     "Invalid date". */
  const pickerValue = (() => {
    if (!value.fixedDate) return "";
    const parsed = moment(value.fixedDate, format);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
  })();

  const setFixedDate = (text) =>
    set({ fixedDate: text ? moment(text, "YYYY-MM-DD").format(format) : null });

  return (
    <div className="rsce-kvt-daterow">
      <div className="rsce-kvt-select-wrap rsce-date-option">
        <select className="rsce-kvt-select" value={value.dateOption} onChange={(e) => setOption(e.target.value)}>
          {DATE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="rsce-kvt-select-caret">{Icon.caret}</span>
      </div>

      <div className="rsce-date-mid">
        {value.dateOption === "daysBefore" && (
          <input
            className="rsce-kvt-input"
            type="number"
            min={1}
            value={value.timeUnits == null ? 1 : value.timeUnits}
            /* Below one would ask for a date after today, which is not what
               "days before" means. */
            onChange={(e) => set({ timeUnits: Math.max(1, Number(e.target.value) || 1) })}
          />
        )}

        {value.dateOption === "today" && (
          <input className="rsce-kvt-input" type="text" value={moment().format(format)} disabled readOnly />
        )}

        {value.dateOption === "fixedDate" && (
          <input
            className={`rsce-kvt-input ${error ? "has-error" : ""}`}
            type="date"
            value={pickerValue}
            onChange={(e) => setFixedDate(e.target.value)}
          />
        )}
      </div>

      {/* Free text with suggestions, not a closed list: the antd form used an
          AutoComplete, so formats outside the three have always been allowed. */}
      <div className="rsce-date-format">
        <input
          className="rsce-kvt-input"
          type="text"
          list="rsce-date-formats"
          placeholder="Date Format"
          value={format}
          onChange={(e) => setFormat(e.target.value)}
        />
        <datalist id="rsce-date-formats">
          {DATE_FORMATS.map((f) => (
            <option key={f} value={f} />
          ))}
        </datalist>
      </div>

      {error && <div className="rsce-date-error">{error}</div>}
    </div>
  );
}
